import axios from 'axios';
import { pendingRequests } from '../utils/offlineStorage';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle offline errors
    if (!error.response && !navigator.onLine) {
      console.log('Offline - queueing request');

      // Queue write operations for later
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(originalRequest.method?.toUpperCase())) {
        try {
          await queueOfflineRequest(originalRequest);

          // Return a special offline response
          return Promise.resolve({
            data: {
              offline: true,
              queued: true,
              message: 'Request queued for when you are back online'
            },
            status: 202,
            statusText: 'Accepted (Offline)',
            config: originalRequest
          });
        } catch (queueError) {
          console.error('Failed to queue request:', queueError);
        }
      }

      // For GET requests, throw a specific offline error
      const offlineError = new Error('You are currently offline');
      offlineError.isOffline = true;
      offlineError.config = originalRequest;
      return Promise.reject(offlineError);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(
          `${api.defaults.baseURL}/auth/refresh`,
          { refresh_token: refreshToken }
        );

        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        // Only redirect if on a protected page that requires authentication
        // Don't redirect from public pages - they should work without login
        const publicPages = ['/', '/contact', '/offline', '/home', '/register'];
        const publicPrefixes = ['/public/', '/portal/', '/vendor/', '/contracts/sign/'];
        const pathname = window.location.pathname;
        const isPublicPage = publicPages.includes(pathname) ||
                             publicPrefixes.some(prefix => pathname.startsWith(prefix)) ||
                             pathname === '/login';
        if (!isPublicPage) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data);
    }

    return Promise.reject(error);
  }
);

/**
 * Queue request for background sync when offline
 */
async function queueOfflineRequest(config) {
  const request = {
    url: config.url,
    method: config.method,
    headers: config.headers,
    data: config.data,
    params: config.params,
    timestamp: Date.now()
  };

  await pendingRequests.add(request);

  // Register background sync if supported
  if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register('sync-requests');
      console.log('Background sync registered');
    } catch (error) {
      console.error('Background sync registration failed:', error);
    }
  }
}

/**
 * Process pending requests when back online
 */
export async function processPendingRequests() {
  if (!navigator.onLine) {
    console.log('Still offline, skipping pending request processing');
    return;
  }

  try {
    const requests = await pendingRequests.getAll();
    console.log(`Processing ${requests.length} pending requests`);

    const results = {
      success: [],
      failed: []
    };

    for (const req of requests) {
      try {
        await api({
          url: req.url,
          method: req.method,
          headers: req.headers,
          data: req.data,
          params: req.params
        });

        results.success.push(req);
        await pendingRequests.remove(req.id);
        console.log('Successfully synced request:', req.id);
      } catch (error) {
        console.error('Failed to sync request:', req.id, error);
        results.failed.push({ request: req, error });
      }
    }

    return results;
  } catch (error) {
    console.error('Failed to process pending requests:', error);
    return null;
  }
}

/**
 * Get count of pending requests
 */
export async function getPendingRequestCount() {
  try {
    const requests = await pendingRequests.getAll();
    return requests.length;
  } catch (error) {
    console.error('Failed to get pending request count:', error);
    return 0;
  }
}

// Set up online event listener to process pending requests
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('Back online - processing pending requests');
    processPendingRequests().then((results) => {
      if (results && results.success.length > 0) {
        console.log(`Successfully synced ${results.success.length} requests`);
      }
    });
  });
}

export default api;
